import type { KeyboardEvent } from "react";
import type { CustomPickerInjectedProps } from "react-color";
import { CustomPicker } from "react-color";
import { Hue, Saturation } from "react-color/lib/components/common";
import { t } from "ttag";

import {
  ControlsRoot,
  HueContainer,
  HuePointer,
  SaturationContainer,
  SaturationPointer,
} from "./ColorPicker.styled";

const saturationStyles = {
  color: {
    borderTopLeftRadius: "5px",
    borderBottomRightRadius: "5px",
  },
};

const HUE_STEP = 1;
const HUE_PAGE_STEP = 10;
const HUE_MAX = 359;
const SATURATION_STEP = 0.01;
const SATURATION_PAGE_STEP = 0.1;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

// react-color's own Hue/Saturation controls are pure mouse/touch drag
// surfaces (verified in node_modules: they wire up only onMouseDown /
// onTouchMove / onTouchStart, nothing keyboard-related) -- a keyboard user
// can never open a color, let alone change it, via these controls alone.
// Both handlers below emit the same color-state shape react-color's own
// pointer-drag `calculateChange` helpers produce (see helpers/hue.js and
// helpers/saturation.js), so `ColorWrap`'s `onChange` (injected as
// `props.onChange`) treats a keyboard-driven change exactly like a drag.
export const ColorPickerControls = CustomPicker(function ColorControls(
  props: CustomPickerInjectedProps,
) {
  const hsl = props.hsl ?? { h: 0, s: 1, l: 0.5, a: 1 };
  const hsv = props.hsv ?? { h: hsl.h, s: 1, v: 1, a: hsl.a };

  const handleHueKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Home") {
      event.preventDefault();
      props.onChange({ h: 0, s: hsl.s, l: hsl.l, a: hsl.a, source: "hsl" });
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      props.onChange({
        h: HUE_MAX,
        s: hsl.s,
        l: hsl.l,
        a: hsl.a,
        source: "hsl",
      });
      return;
    }

    const delta = {
      ArrowLeft: -HUE_STEP,
      ArrowDown: -HUE_STEP,
      ArrowRight: HUE_STEP,
      ArrowUp: HUE_STEP,
      PageDown: -HUE_PAGE_STEP,
      PageUp: HUE_PAGE_STEP,
    }[event.key];
    if (delta == null) {
      return;
    }

    event.preventDefault();
    props.onChange({
      h: clamp(hsl.h + delta, 0, HUE_MAX),
      s: hsl.s,
      l: hsl.l,
      a: hsl.a,
      source: "hsl",
    });
  };

  const handleSaturationKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Home") {
      event.preventDefault();
      props.onChange({ h: hsv.h, s: 0, v: hsv.v, a: hsv.a, source: "hsv" });
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      props.onChange({ h: hsv.h, s: 1, v: hsv.v, a: hsv.a, source: "hsv" });
      return;
    }

    // Left/Right move saturation, Up/Down move brightness -- matching the
    // pointer's own axes (helpers/saturation.js: left% = saturation,
    // 1 - top% = brightness). Page Up/Down take a larger step on
    // saturation, the axis ARIA's 1-D `slider` role can least misrepresent.
    let deltaS = 0;
    let deltaV = 0;
    switch (event.key) {
      case "ArrowLeft":
        deltaS = -SATURATION_STEP;
        break;
      case "ArrowRight":
        deltaS = SATURATION_STEP;
        break;
      case "ArrowUp":
        deltaV = SATURATION_STEP;
        break;
      case "ArrowDown":
        deltaV = -SATURATION_STEP;
        break;
      case "PageDown":
        deltaS = -SATURATION_PAGE_STEP;
        break;
      case "PageUp":
        deltaS = SATURATION_PAGE_STEP;
        break;
      default:
        return;
    }

    event.preventDefault();
    props.onChange({
      h: hsv.h,
      s: clamp(hsv.s + deltaS, 0, 1),
      v: clamp(hsv.v + deltaV, 0, 1),
      a: hsv.a,
      source: "hsv",
    });
  };

  return (
    <ControlsRoot>
      {/* ARIA has no native 2-D slider role -- `slider` plus a combined
      `aria-valuetext` announcing both axes is the documented compromise. */}
      <SaturationContainer
        role="slider"
        tabIndex={0}
        aria-label={t`Color saturation and brightness`}
        aria-valuetext={t`Saturation ${Math.round(hsv.s * 100)}%, brightness ${Math.round(hsv.v * 100)}%`}
        onKeyDown={handleSaturationKeyDown}
      >
        <Saturation
          {...props}
          pointer={SaturationPointer}
          style={saturationStyles}
        />
      </SaturationContainer>
      <HueContainer
        role="slider"
        tabIndex={0}
        aria-label={t`Hue`}
        aria-orientation="horizontal"
        aria-valuemin={0}
        aria-valuemax={HUE_MAX}
        aria-valuenow={Math.round(hsl.h)}
        onKeyDown={handleHueKeyDown}
      >
        <Hue {...props} pointer={HuePointer} />
      </HueContainer>
    </ControlsRoot>
  );
});
